import { LoginConsts } from 'client/util/Constants';
import MfaConstants from 'client/containers/Login/MfaConstants';
import RouteGenerator from 'client/routes/RouteGenerator';
import getTextByMFATypeForContext from 'client/containers/Login/mfaStringUtilities';

const { user } = LoginConsts.clientType;
const { mfaStatus, mfaNames } = MfaConstants;

/**
 * In JumpCloud business logic there is a seperation between "Admin" users and "System" users.
 * The seperation is between who a user is rather than what a user is doing. This makes sense
 * in some ways and not in others. One way it doesn't make sense is during MFA verification.
 * The only difference between an Admin and a System user during MFA verification is where the
 * verification result is written in a database. That seperation between user types has
 * filtered all the way to the UI, where we need logic for different types of MFA verification
 * though the operations to determine that verification are exactly the same on the backend.
 */

export class Duo {
  constructor(factor = {
    clientType: user,
    status: mfaStatus.unavailable,
  }, options = {}) {
    const {
      redirectTo = '',
      challenge = {},
      passwordResetInfo = null,
      context = '',
    } = options;

    this.name = 'duo';
    this.description = 'Duo Security';
    this.status = (factor && factor.status) || mfaStatus.unavailable;

    let mappedChallenge;
    if ((passwordResetInfo && passwordResetInfo.confirmationCode) && (challenge && challenge.duo)) {
      mappedChallenge = challenge.duo;
    } else {
      mappedChallenge = challenge;
    }

    this.route = {
      name: RouteGenerator.duo2fa,
      params: {
        clientType: user,
        challenge: mappedChallenge,
        passwordResetInfo,
        available: factor.status !== mfaStatus.unavailable,
        context,
      },
    };

    this.iconUrl = '/login/img/icons/duo-logo.svg';

    if (redirectTo) {
      this.route.params.redirectTo = redirectTo;
    }

    return this;
  }
}

export class Push {
  constructor(factor = {
    clientType: user,
    status: mfaStatus.unavailable,
  }, options = {}) {
    const {
      redirectTo = '',
      passwordResetInfo = {},
      token = '',
      context = '',
    } = options;

    this.name = 'push';
    this.description = 'JumpCloud Protect';
    this.status = (factor && factor.status) || mfaStatus.unavailable;
    this.token = token;

    const mfaDisplayText = getTextByMFATypeForContext(this.name)(context);

    this.route = {
      name: RouteGenerator.push,
      params: {
        clientType: (factor && factor.clientType) || user,
        context,
        mfaDisplayText,
        passwordResetInfo,
        token,
      },
    };

    this.iconUrl = '/login/img/icons/icon-jumpcloud-protect-secondary.svg';

    if (redirectTo) {
      this.route.params.redirectTo = redirectTo;
    }

    return this;
  }
}

export class Totp {
  constructor(factor = {
    clientType: user,
    status: mfaStatus.unavailable,
  }, options = {}) {
    const {
      redirectTo = '',
      passwordResetInfo = {},
      context = '',
    } = options;

    this.name = 'totp';
    this.description = 'Use an Authenticator App';
    this.status = (factor && factor.status) || mfaStatus.unavailable;

    const mfaDisplayText = getTextByMFATypeForContext(this.name)(context);

    this.route = {
      name: RouteGenerator.totp,
      params: {
        clientType: (factor && factor.clientType) || user,
        context,
        mfaDisplayText,
        passwordResetInfo,
      },
    };

    this.iconUrl = '/login/img/icons/icon-totp.svg';

    if (redirectTo) {
      this.route.params.redirectTo = redirectTo;
    }

    return this;
  }
}

export class WebAuthn {
  constructor(factor = {
    clientType: user,
    status: mfaStatus.unavailable,
  }, options = {}) {
    const {
      redirectTo = '',
      challenge = {},
      passwordResetInfo = {},
      context = '',
    } = options;

    this.name = 'webauthn';
    this.description = 'Security Key or Built-In Sensor';
    this.status = (factor && factor.status) || mfaStatus.unavailable;

    this.challenge = challenge;

    const mfaDisplayText = getTextByMFATypeForContext(this.name)(context);

    this.route = {
      name: RouteGenerator.webauthn,
      params: {
        available: factor.status !== mfaStatus.unavailable,
        challenge: this.challenge,
        clientType: user,
        context,
        mfaDisplayText,
        passwordResetInfo,
      },
    };

    this.iconUrl = '/login/img/icons/icon-security-key.svg';

    if (redirectTo) {
      this.route.params.redirectTo = redirectTo;
    }

    return this;
  }
}

const MfaTypes = new Map([
  [mfaNames.duo, Duo],
  [mfaNames.push, Push],
  [mfaNames.totp, Totp],
  [mfaNames.webauthn, WebAuthn],
]);

export default function MfaType(mfa = { factor: { type: mfaNames.totp } }, options = {}) {
  const name = (mfa.factor && mfa.factor.type && mfaNames[mfa.factor.type]) || mfaNames.totp;

  const constructor = MfaTypes.get(name);

  return new constructor(
    mfa.factor,
    {
      redirectTo: options.redirectTo,
      challenge: mfa.challenge,
      passwordResetInfo: options.passwordResetInfo,
      context: options.context,
    },
  );
}
